import { hits } from '../song';
import type { Note } from '../song';
import { transpose } from '../score';
import { DREAM_PLAN, KEYS, MAJOR, flavored } from './dream';
import type { Bar, Chord, Ctx } from './dream';

const CHORDS: Readonly<Record<string, Chord>> = {
  C6: { root: 'C2', pad: ['E3', 'G3', 'A3', 'C4'] },
  Am: { root: 'A1', pad: ['C3', 'E3', 'A3', 'C4'] },
  Dm7: { root: 'D2', pad: ['F3', 'A3', 'C4', 'D4'] },
  G: { root: 'G1', pad: ['D3', 'G3', 'B3', 'D4'] },
  Fadd9: { root: 'F1', pad: ['F3', 'A3', 'C4', 'G4'] },
  Em7: { root: 'E2', pad: ['E3', 'G3', 'B3', 'D4'] },
};

const HOOK: Note[][] = [
  [[0, 'G5', 2], [2, 'E5', 1], [3, 'G5', 1], [4, 'A5', 2], [6, 'G5', 2], [8, 'E5', 2], [10, 'C5', 2], [12, 'D5', 2], [14, 'E5', 2]],
  [[0, 'A5', 2], [2, 'C6', 2], [4, 'A5', 1], [5, 'G5', 1], [6, 'E5', 2], [8, 'A5', 3], [12, 'G5', 2], [14, 'E5', 2]],
  [[0, 'F5', 2], [2, 'A5', 2], [4, 'D5', 2], [6, 'F5', 2], [8, 'A5', 2], [10, 'C6', 2], [12, 'A5', 4]],
  [[0, 'B5', 2], [2, 'G5', 2], [4, 'D5', 2], [6, 'G5', 1], [7, 'A5', 1], [8, 'B5', 4], [12, 'D6', 2], [14, 'B5', 2]],
];

const CLAVE = 'x..x..x...x.x...';

function extra(x: Ctx, b: Bar): void {
  const jit = () => 0.9 + x.rng.float() * 0.2;
  if (b.end) {
    x.hit('wood', b.bar, 0, 0.8);
    return;
  }
  if (b.drop || b.groove) {
    for (const h of hits(CLAVE)) x.hit('wood', b.bar, h.step, (b.drop ? 0.85 : 0.62) * jit());
    if (b.drop && x.e >= 0.6) {
      const n = x.e >= 0.85 ? 2 : 1;
      for (let k = 0; k < n; k++) x.play('pop', b.bar, 1 + 2 * x.rng.int(0, 8), transpose(x.rng.pick(b.bells), 12), x.s * 2, 0.55 * jit());
    }
  } else if (b.name === 'break') {
    x.hit('wood', b.bar, 12, 0.5);
    for (let k = 0; k < 3; k++) x.play('pop', b.bar, x.rng.int(0, 16), transpose(x.rng.pick(b.bells), 12), x.s * 3, 0.5 * jit());
  } else if (b.name === 'intro' && b.index >= b.bars / 2) {
    for (const h of hits('x.....x.........')) x.hit('wood', b.bar, h.step, 0.5 * jit());
  }
}

export const COGUMELOS = flavored({
  id: 'cogumelos',
  genre: 'forest whimsy / marimba pop',
  bpm: [116, 124],
  ref: { seed: 'clareira', title: 'Clareira', params: { bpm: 118, bars: 28, energy: 0.55 } },
  chords: CHORDS,
  progs: [
    ['C6', 'Am', 'Dm7', 'G'],
    ['C6', 'Fadd9', 'G', 'Am'],
    ['Fadd9', 'G', 'Em7', 'Am'],
    ['C6', 'G', 'Am', 'Fadd9'],
  ],
  hook: HOOK,
  arps: [
    [0, 2, 1, 3, 2, 1],
    [0, 1, 2, 3, 1, 2, 3, 0],
    [3, 1, 2, 0, 2, 1],
  ],
  keys: KEYS,
  scale: MAJOR,
  range: [72, 88],
  center: 79,
  plan: DREAM_PLAN,
  swing: 58,
  lufs: -11.5,
  mix: (bpm) => ({ reverbDecay: 2.6, delayTime: (60 / bpm) * 0.5, delayFeedback: 0.22, drive: 1.15, softness: 1.4, pumpDepth: 0.16, pumpRelease: 0.22 }),
  kick: { groove: 'X.......X.....x.', drop: 'X.....x.X.....x.' },
  back: { groove: '....x.......x...', drop: '....x.......x...', vel: [0.6, 0.8] },
  hats: ['o-x-o-x-o-x-o-x-', 'oxxxoxxxoxxxoxxx', 'o-xxo-xxo-xxo-xx'],
  open: { pattern: '....x.......x...', from: 0.75 },
  bass: {
    groove: [
      [0, 0, 2, 0.9],
      [6, 7, 1.5, 0.7],
      [8, 0, 2, 0.85],
      [14, 7, 1.5, 0.7],
    ],
    drop: [
      [0, 0, 2, 0.9],
      [3, 7, 1.5, 0.7],
      [6, 12, 1.5, 0.75],
      [8, 0, 2, 0.85],
      [11, 7, 1.5, 0.7],
      [14, 12, 1.5, 0.7],
    ],
  },
  arp: { groove: 'X..x..x.X..x..x.', drop: 'X.xX.xX.X.xX.xX.', len: 1.2 },
  bells: (pad) => pad.map((n) => transpose(n, 12)),
  extra,
  accents: { kick: 'kick', snare: 'snare', pad: 'stab', lead: 'melody', impact: 'drop' },
});
